import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useI18n } from "@/lib/i18n";
import { FadeUp, StaggerWords } from "@/components/Motion";
import BeePattern from "@/components/BeePattern";

export default function OriginSection() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section
      ref={ref}
      className="relative py-32 lg:py-40 border-t border-white/5 bg-[#0A0A0A] overflow-hidden"
      data-testid="origin-section"
    >
      <BeePattern opacity={0.22} size="480px" vignette="linear" />
      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12">
        <FadeUp className="mb-20">
          <h2 className="font-editorial italic font-light text-4xl sm:text-5xl lg:text-7xl text-cream leading-[1.05] max-w-4xl">
            <StaggerWords text={t.origin.title} />
          </h2>
        </FadeUp>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          {/* LEFT: two staggered images */}
          <div className="lg:col-span-6 relative">
            <motion.figure style={{ y: y1 }} className="relative w-[85%] mb-6">
              <div className="overflow-hidden clip-arch aspect-[3/5]">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/rrkqpwlp_WhatsApp%20Image%202026-07-27%20at%2012.47.09.jpeg"
                  alt="The old stone house — Val di Fassa"
                  className="w-full h-full object-cover"
                />
              </div>
              <figcaption className="mt-4 font-display text-[9px] tracking-[0.35em] text-cream/50">
                — {t.origin.cap1}
              </figcaption>
            </motion.figure>

            <motion.figure
              style={{ y: y2 }}
              className="relative w-[75%] ml-auto -mt-32 lg:-mt-40 z-10"
            >
              <div className="overflow-hidden clip-arch aspect-[3/5]">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/2fjt5cw2_WhatsApp%20Image%202026-07-27%20at%2016.47.09%281%29.webp"
                  alt="Beekeeper at the hives — forest"
                  className="w-full h-full object-cover"
                />
              </div>
              <figcaption className="mt-4 font-display text-[9px] tracking-[0.35em] text-cream/50 text-right">
                — {t.origin.cap2}
              </figcaption>
            </motion.figure>
          </div>

          {/* RIGHT: story text */}
          <FadeUp className="lg:col-span-6 lg:pl-10 lg:pt-8" delay={0.1}>
            <div className="space-y-8 text-cream/75 font-body text-base sm:text-lg leading-[1.75] max-w-xl">
              <p dangerouslySetInnerHTML={{ __html: t.origin.p1 }} />
              <p dangerouslySetInnerHTML={{ __html: t.origin.p2 }} />
              <p dangerouslySetInnerHTML={{ __html: t.origin.p3 }} />
              {t.origin.p4 && <p dangerouslySetInnerHTML={{ __html: t.origin.p4 }} />}
            </div>

            <div className="mt-14 pl-6 border-l border-gold/40 max-w-xl">
              <p className="font-editorial italic text-2xl sm:text-3xl text-gold leading-snug">
                {t.origin.pull}
              </p>
            </div>

            <div className="mt-10 flex items-center gap-4 font-display text-[10px] tracking-[0.35em] text-cream/60">
              <span className="w-8 h-px bg-cream/30" />
              <span>MADU · Honig — BAU · Familie</span>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
